import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Loader2, MailX, XCircle } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

type State = "validating" | "ready" | "submitting" | "done" | "invalid" | "error";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>("validating");
  const [email, setEmail] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setMessage("This unsubscribe link is missing its code.");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_KEY } },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error) {
          setState("invalid");
          setMessage(data?.error || "This unsubscribe link is no longer valid.");
          return;
        }
        setEmail(data?.email ?? null);
        setState("ready");
      } catch {
        setState("error");
        setMessage("We could not check this link. Please try again.");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    if (error || data?.error) {
      setState("error");
      setMessage(data?.error || error?.message || "We could not complete your request.");
      return;
    }
    setState("done");
  };

  return (
    <>
      <SEO
        title="Unsubscribe | Veylo AI"
        description="Manage your Veylo AI email preferences and unsubscribe from notifications."
        path="/unsubscribe"
      />
      <main className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              {state === "done" ? (
                <CheckCircle2 className="h-6 w-6 text-primary" />
              ) : state === "invalid" || state === "error" ? (
                <XCircle className="h-6 w-6 text-destructive" />
              ) : (
                <MailX className="h-6 w-6 text-primary" />
              )}
            </div>
            <CardTitle>
              {state === "done" ? "You're unsubscribed" : "Unsubscribe from emails"}
            </CardTitle>
            <CardDescription>
              {state === "validating" && "Checking your link..."}
              {state === "ready" &&
                (email
                  ? `Stop sending emails to ${email}?`
                  : "Confirm that you want to stop receiving these emails.")}
              {state === "submitting" && "Updating your preferences..."}
              {state === "done" &&
                "You will no longer receive these emails. You can still receive account and security messages."}
              {(state === "invalid" || state === "error") && message}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {(state === "validating" || state === "submitting") && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {state === "ready" && (
              <Button onClick={confirm} className="w-full">
                Confirm unsubscribe
              </Button>
            )}
            {state === "error" && (
              <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
                Try again
              </Button>
            )}
            <Button asChild variant="ghost" className="w-full">
              <a href="/">Back to Veylo AI</a>
            </Button>
          </CardContent>
        </Card>
      </main>
    </>
  );
};

export default Unsubscribe;
